import { useEffect, useState } from 'react';
import { hasPermission } from '@/types';
import { useParams, useNavigate } from 'react-router-dom';
import { vehicles as api } from '@/api/endpoints';
import type { Vehicle, VehicleMeta, VehicleService, VehicleServiceInput, VehicleServiceType, VehicleServiceStatus,
  VehicleOdometerReading, VehicleOdometerReadingInput, OdometerSource } from '@/types';
import { Icon } from '@/components/Icon';
import { SearchableSelect } from '@/components/SearchableSelect';
import { ArchiveFilesCard } from '@/components/ArchiveFilesCard';
import { useAuth } from '@/auth/AuthContext';
import { nearestBadge } from './KadryPage';
import { ModalShell } from './KadryDetailPage';
import { VehicleModal } from './PojazdyPage';
import { formatCurrency } from '@/lib/money';

type Tab = 'services' | 'readings' | 'files';

export function PojazdyDetailPage() {
  const { id } = useParams();
  const vehId = Number(id);
  const navigate = useNavigate();
  const { user } = useAuth();
  const canEdit = hasPermission(user, 'FLEET_MANAGE');

  const [veh, setVeh] = useState<Vehicle | null>(null);
  const [meta, setMeta] = useState<VehicleMeta | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>('services');
  const [editVeh, setEditVeh] = useState(false);
  const [serviceModal, setServiceModal] = useState<VehicleService | 'new' | null>(null);
  const [readings, setReadings] = useState<VehicleOdometerReading[]>([]);
  const [readingModal, setReadingModal] = useState(false);

  const reload = () => { api.get(vehId).then(setVeh).catch(e => setErr(e?.message ?? String(e))); };
  const reloadReadings = () => { api.readings.list(vehId).then(setReadings).catch(() => {}); };
  useEffect(() => { reload(); reloadReadings(); api.meta().then(setMeta).catch(() => {}); /* eslint-disable-next-line */ }, [vehId]);

  if (err) return <div className="text-red-500">Błąd: {err}</div>;
  if (!veh) return <div className="text-ink-400">Ładowanie…</div>;

  const onDelService = async (s: VehicleService) => {
    if (!confirm(`Usunąć przegląd „${s.typeName}"?`)) return;
    try { await api.services.remove(vehId, s.id); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };

  const onDelReading = async (r: VehicleOdometerReading) => {
    if (!confirm(`Usunąć odczyt z ${r.readingDate}?`)) return;
    try { await api.readings.remove(vehId, r.id); reloadReadings(); reload(); }
    catch (e) { alert('Błąd: ' + (e instanceof Error ? e.message : String(e))); }
  };

  const ownershipLine = veh.ownershipType && veh.ownershipType !== 'OWNED'
    ? [veh.ownershipTypeName, veh.financingCompany, veh.financingContractNo,
       veh.financingMonthlyCost != null ? formatCurrency(veh.financingMonthlyCost) + '/mc' : null].filter(Boolean).join(' · ')
    : (veh.ownershipTypeName ?? '—');

  return (
    <div className="max-w-[1100px]">
      <button onClick={() => navigate('/pojazdy')} className="btn btn-ghost btn-sm mb-3 text-ink-500">
        <Icon name="arrow" className="w-3.5 h-3.5 rotate-180"/> Pojazdy i sprzęt
      </button>

      <header className="flex items-start justify-between mb-5 flex-wrap gap-3">
        <div>
          <h1 className="mb-1">{veh.label}</h1>
          <div className="text-ink-500 text-[13px]">
            {veh.typeName}{veh.brand ? ' · ' + veh.brand : ''}{veh.model ? ' ' + veh.model : ''}
            <span className="ml-2 pill" style={{ background: '#eef2ff' }}>{veh.statusName}</span>
          </div>
          <div className="text-ink-400 text-[12px] mt-1">
            {[veh.registrationNumber ? 'nr rej. ' + veh.registrationNumber : null,
              veh.productionYear ? 'rok ' + veh.productionYear : null,
              veh.assignedEmployeeName ? 'operator: ' + veh.assignedEmployeeName : null].filter(Boolean).join(' · ') || '—'}
          </div>
        </div>
        {canEdit && (
          <button onClick={() => setEditVeh(true)} className="btn btn-secondary btn-sm">
            <Icon name="edit" className="w-3.5 h-3.5"/> Edytuj dane
          </button>
        )}
      </header>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_1fr] gap-4 mb-5">
        <div className="card"><div className="card-body text-[13px] grid grid-cols-2 gap-y-1.5">
          <KV k="VIN" v={veh.vin}/>
          <KV k="Przebieg / mth" v={veh.odometer != null ? String(veh.odometer) : null}/>
          <KV k="Data nabycia" v={veh.acquisitionDate}/>
          <KV k="Firma" v={veh.companyName}/>
          <KV k="Władanie" v={ownershipLine}/>
          <KV k="Ubezpieczyciel" v={veh.insurer}/>
          <KV k="Karta paliwowa" v={veh.fuelCardNumber}/>
          <KV k="E-toll ID" v={veh.etollId}/>
          {/* Puste = powiązanie powstaje automatycznie po nr rejestracyjnym; pokazujemy tylko
              wskazanie RĘCZNE, bo tylko ono jest decyzją zapisaną w karcie pojazdu. */}
          {veh.gpsDeviceId && <KV k="Urządzenie GPS" v={veh.gpsDeviceId}/>}
          {veh.adrValidUntil && <KV k="ADR ważne do" v={veh.adrValidUntil}/>}
        </div></div>
        {veh.notes && <div className="card"><div className="card-body text-[13px] text-ink-600 whitespace-pre-wrap">{veh.notes}</div></div>}
      </div>

      <div className="flex gap-1 mb-3 border-b border-ink-100">
        {([['services', 'Przeglądy i badania', veh.services.length], ['readings', 'Przebieg / mth', readings.length], ['files', 'Pliki', null]] as [Tab, string, number | null][]).map(([t, label, count]) => (
          <button key={t} onClick={() => setTab(t)}
            className={'px-3 py-2 text-[13px] border-b-2 -mb-px ' + (tab === t ? 'border-water-500 text-ink-900 font-semibold' : 'border-transparent text-ink-500 hover:text-ink-700')}>
            {label}{count != null ? ` (${count})` : ''}
          </button>
        ))}
      </div>

      {tab === 'services' && (
        <div className="card"><div className="card-body !p-0">
          <div className="flex items-center px-4 py-2 border-b border-ink-100">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500">Przeglądy, badania, ubezpieczenia</span>
            {canEdit && <button onClick={() => setServiceModal('new')} className="ml-auto btn btn-secondary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj</button>}
          </div>
          {veh.services.length === 0 ? <div className="p-5 text-center text-ink-400 text-[12px]">Brak przeglądów.</div> : (
            <table className="table-hw">
              <thead><tr><th className="w-48">Rodzaj</th><th className="w-28">Ostatnio</th><th className="w-40">Ważne do</th><th>Koszt</th><th className="w-24">Status</th><th className="w-20"></th></tr></thead>
              <tbody>
                {veh.services.map(s => (
                  <tr key={s.id}>
                    <td><div className="font-semibold text-ink-900 text-[13px]">{s.typeName}</div>{s.name && <div className="text-[11px] text-ink-400">{s.name}</div>}</td>
                    <td className="font-mono text-[12px]">{s.lastDate || '—'}</td>
                    <td className="font-mono text-[12px]">
                      {s.nextDate ? <div className="flex flex-col leading-tight"><span>{s.nextDate}</span>{nearestBadge(s.daysUntil, s.urgency)}</div> : <span className="text-ink-400">—</span>}
                    </td>
                    <td className="text-[12px]">{formatCurrency(s.cost)}</td>
                    <td><span className="pill" style={{ background: s.statusColor === 'green' ? '#dcfce7' : s.statusColor === 'red' ? '#fee2e2' : '#f1f5f9', color: s.statusColor === 'green' ? '#15803d' : s.statusColor === 'red' ? '#b91c1c' : '#64748b' }}>{s.statusName}</span></td>
                    <td>{canEdit && <div className="flex gap-1 justify-end">
                      <button onClick={() => setServiceModal(s)} className="btn btn-ghost btn-icon text-ink-400 hover:text-water-600"><Icon name="edit" className="w-3.5 h-3.5"/></button>
                      <button onClick={() => onDelService(s)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>
                    </div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
      )}

      {tab === 'readings' && (
        <div className="card"><div className="card-body !p-0">
          <div className="flex items-center px-4 py-2 border-b border-ink-100">
            <span className="text-[11px] uppercase tracking-wider font-semibold text-ink-500">Historia przebiegu / motogodzin</span>
            {canEdit && <button onClick={() => setReadingModal(true)} className="ml-auto btn btn-secondary btn-sm">
              <Icon name="plus" className="w-3.5 h-3.5"/> Dodaj odczyt</button>}
          </div>
          {readings.length === 0 ? <div className="p-5 text-center text-ink-400 text-[12px]">Brak odczytów. Dodaj odczyt przebiegu lub zaimportuj ze zdjęć liczników.</div> : (
            <table className="table-hw">
              <thead><tr><th className="w-32">Data</th><th className="w-32">Przebieg [km]</th><th className="w-32">Mth</th><th className="w-40">Podzespół</th><th className="w-32">Źródło</th><th>Notatka</th><th className="w-12"></th></tr></thead>
              <tbody>
                {readings.map(r => (
                  <tr key={r.id}>
                    <td className="font-mono text-[12px]">{r.readingDate}</td>
                    <td className="font-mono text-[12px]">{r.odometer != null ? r.odometer.toLocaleString('pl-PL') : '—'}</td>
                    <td className="font-mono text-[12px]">{r.engineHours != null ? r.engineHours : '—'}</td>
                    <td className="text-[12px] text-ink-600">{r.component || '—'}</td>
                    <td className="text-[12px] text-ink-400">{r.sourceName}</td>
                    <td className="text-[12px] text-ink-500">{r.note || ''}</td>
                    <td>{canEdit && <button onClick={() => onDelReading(r)} className="btn btn-ghost btn-icon text-ink-400 hover:text-red-500"><Icon name="x" className="w-3.5 h-3.5"/></button>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div></div>
      )}

      {tab === 'files' && <ArchiveFilesCard scope="vehicle" scopeId={veh.id} scopeName={veh.label}/>}

      {editVeh && <VehicleModal item={veh} onClose={() => setEditVeh(false)} onSaved={() => { setEditVeh(false); reload(); }}/>}
      {serviceModal && meta && (
        <ServiceModal vehicleId={vehId} item={serviceModal === 'new' ? null : serviceModal} meta={meta}
          onClose={() => setServiceModal(null)} onSaved={() => { setServiceModal(null); reload(); }}/>
      )}
      {readingModal && meta && (
        <ReadingModal vehicleId={vehId} meta={meta}
          onClose={() => setReadingModal(false)} onSaved={() => { setReadingModal(false); reloadReadings(); reload(); }}/>
      )}
    </div>
  );
}

function ReadingModal({ vehicleId, meta, onClose, onSaved }: {
  vehicleId: number; meta: VehicleMeta; onClose: () => void; onSaved: () => void;
}) {
  const [readingDate, setReadingDate] = useState('');
  const [odometer, setOdometer] = useState('');
  const [engineHours, setEngineHours] = useState('');
  const [component, setComponent] = useState('');
  const [source, setSource] = useState<OdometerSource>('MANUAL');
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!readingDate) { setErr('Data odczytu jest wymagana'); return; }
    if (odometer === '' && engineHours === '') { setErr('Podaj przebieg [km] lub motogodziny [mth]'); return; }
    setBusy(true); setErr(null);
    const payload: VehicleOdometerReadingInput = {
      readingDate,
      odometer: odometer === '' ? null : Number(odometer),
      engineHours: engineHours === '' ? null : Number(engineHours),
      component: component.trim() || null, source, note: note.trim() || null,
    };
    try { await api.readings.add(vehicleId, payload); onSaved(); }
    catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  return (
    <ModalShell title="Nowy odczyt przebiegu" onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ readingDate, odometer, engineHours, component, source, note }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Data odczytu *</span>
          <input type="date" value={readingDate} onChange={e => setReadingDate(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Źródło</span>
          <SearchableSelect value={source} onChange={v => setSource(v as OdometerSource)}>
            {(meta.odometerSources ?? [{ id: 'MANUAL', name: 'Ręczny' }]).map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SearchableSelect></label>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Przebieg [km]</span>
          <input type="number" min="0" value={odometer} onChange={e => setOdometer(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Motogodziny [mth]</span>
          <input type="number" min="0" step="0.1" value={engineHours} onChange={e => setEngineHours(e.target.value)} className="input"/></label>
      </div>
      <label className="field"><span className="field-label">Podzespół (opcjonalnie)</span>
        <input value={component} onChange={e => setComponent(e.target.value)} className="input" placeholder="np. silnik / pompa / kompresor / żuraw"/></label>
      <label className="field"><span className="field-label">Notatka</span>
        <input value={note} onChange={e => setNote(e.target.value)} className="input"/></label>
    </ModalShell>
  );
}

function KV({ k, v }: { k: string; v?: string | null }) {
  return <><div className="text-ink-400">{k}</div><div className="text-ink-800">{v || '—'}</div></>;
}

function ServiceModal({ vehicleId, item, meta, onClose, onSaved }: {
  vehicleId: number; item: VehicleService | null; meta: VehicleMeta;
  onClose: () => void; onSaved: () => void;
}) {
  const isEdit = !!item;
  const [type, setType] = useState<VehicleServiceType>(item?.type ?? 'TECHNICAL_INSPECTION');
  const [name, setName] = useState(item?.name ?? '');
  const [lastDate, setLastDate] = useState(item?.lastDate ?? '');
  const [nextDate, setNextDate] = useState(item?.nextDate ?? '');
  const [odometerAtService, setOdometerAtService] = useState<string>(item?.odometerAtService != null ? String(item.odometerAtService) : '');
  const [remindDaysBefore, setRemindDaysBefore] = useState<number>(item?.remindDaysBefore ?? 30);
  const [cost, setCost] = useState<string>(item?.cost != null ? String(item.cost) : '');
  const [status, setStatus] = useState<VehicleServiceStatus>(item?.status ?? 'VALID');
  const [notes, setNotes] = useState(item?.notes ?? '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    const payload: VehicleServiceInput = {
      type, name: name.trim() || null, lastDate: lastDate || null, nextDate: nextDate || null,
      odometerAtService: odometerAtService === '' ? null : Number(odometerAtService),
      remindDaysBefore: Number(remindDaysBefore) || 30,
      cost: cost === '' ? null : Number(cost), status, notes: notes.trim() || null,
    };
    try {
      if (isEdit) await api.services.update(vehicleId, item!.id, payload);
      else await api.services.add(vehicleId, payload);
      onSaved();
    } catch (er) { setErr(er instanceof Error ? er.message : String(er)); setBusy(false); }
  };

  return (
    <ModalShell title={isEdit ? 'Edytuj przegląd' : 'Nowy przegląd / badanie'} onClose={onClose} onSubmit={submit} busy={busy} err={err} draftValue={{ type, name, lastDate, nextDate, odometerAtService, remindDaysBefore, cost, status, notes }}>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Rodzaj *</span>
          <SearchableSelect value={type} onChange={v => setType(v as VehicleServiceType)}>
            {meta.serviceTypes.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
          </SearchableSelect></label>
        <label className="field"><span className="field-label">Status</span>
          <SearchableSelect value={status} onChange={v => setStatus(v as VehicleServiceStatus)}>
            {meta.serviceStatuses.map(s => <option key={s.id} value={s.id}>{s.name}</option>)}
          </SearchableSelect></label>
      </div>
      <label className="field"><span className="field-label">Opis (opcjonalnie)</span>
        <input value={name} onChange={e => setName(e.target.value)} className="input" placeholder="np. polisa nr ..., stacja diagnostyczna ..."/></label>
      <div className="grid grid-cols-2 gap-3">
        <label className="field"><span className="field-label">Data (ostatnie)</span>
          <input type="date" value={lastDate} onChange={e => setLastDate(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Ważne do (następny termin)</span>
          <input type="date" value={nextDate} onChange={e => setNextDate(e.target.value)} className="input"/></label>
      </div>
      <div className="grid grid-cols-3 gap-3">
        <label className="field"><span className="field-label">Przebieg przy przeglądzie</span>
          <input type="number" value={odometerAtService} onChange={e => setOdometerAtService(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Koszt (PLN)</span>
          <input type="number" min="0" step="0.01" value={cost} onChange={e => setCost(e.target.value)} className="input"/></label>
        <label className="field"><span className="field-label">Przypominaj (dni przed)</span>
          <input type="number" min="0" step="1" value={remindDaysBefore} onChange={e => setRemindDaysBefore(Number(e.target.value))} className="input"/></label>
      </div>
      <label className="field"><span className="field-label">Notatki</span>
        <textarea value={notes} onChange={e => setNotes(e.target.value)} className="textarea" rows={2}/></label>
    </ModalShell>
  );
}
